import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, CloudUpload as CloudUploadIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import vendedorService from '../../services/vendedorService';
import * as XLSX from 'xlsx';

const Vendedores: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [vendedores, setVendedores] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [_loading, setLoading] = useState(false);

  const fetchVendedores = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const data = await vendedorService.getVendedoresPaged({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search
      });
      setVendedores(data.items);
      setTotalCount(data.totalCount);
    } catch (error: any) {
      console.error('Error al cargar vendedores:', error);
      enqueueSnackbar('Error al cargar vendedores', { variant: 'error' });
      setVendedores([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendedores();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchVendedores(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchVendedores(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchVendedores(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (nroVendedor: number) => {
    if (window.confirm('¿Está seguro de eliminar este vendedor?')) {
      try {
        await vendedorService.deleteVendedor(nroVendedor);
        enqueueSnackbar('Vendedor eliminado exitosamente', { variant: 'success' });
        fetchVendedores();
      } catch (error: any) {
        console.error('Error al eliminar vendedor:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar vendedor', { variant: 'error' });
      }
    }
  };

  const handleExportExcel = async () => {
    try {
      const data = await vendedorService.getVendedoresPaged({
        pageNumber: 1,
        pageSize: 10000,
        searchTerm: searchTerm
      });
      const dataToExport = data.items.map((v: any) => ({
      'N° Vendedor': v.nroVendedor,
      'Nombre': v.nombreVendedor,
      'Legajo': v.legajo,
      'Tipo': v.tipoVendedor,
      'Comisión %': v.comisionPorc
    }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Vendedores');
      XLSX.writeFile(wb, 'Vendedores.xlsx');
      enqueueSnackbar('Archivo exportado exitosamente', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error al exportar datos', { variant: 'error' });
    }
  };

  const handleImportExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        let successCount = 0;
        let errorCount = 0;

        for (const row of jsonData as any[]) {
          try {
            const vendedor = {
              nroVendedor: Number(row['N° Vendedor'] || row['nroVendedor']),
              nombreVendedor: row['Nombre'] || row['nombreVendedor'],
              legajo: row['Legajo'] || row['legajo'],
              tipoVendedor: row['Tipo'] || row['tipoVendedor'] || 'Interno',
              comisionPorc: Number(row['Comisión %'] || row['comisionPorc'] || 0)
            };

            await vendedorService.createVendedor(vendedor);
            successCount++;
          } catch (error) {
            errorCount++;
            console.error('Error al importar vendedor:', error);
          }
        }

        enqueueSnackbar(`Importación completada: ${successCount} exitosos, ${errorCount} errores`, { 
          variant: errorCount > 0 ? 'warning' : 'success' 
        });
        fetchVendedores();
      } catch (error) {
        enqueueSnackbar('Error al procesar el archivo Excel', { variant: 'error' });
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Vendedores
            </Typography>
            <Stack direction="row" spacing={1}>
              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="import-excel-vendedores"
                type="file"
                onChange={handleImportExcel}
              />
              <label htmlFor="import-excel-vendedores">
                <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} size="small">
                  Importar
                </Button>
              </label>
              <Button variant="outlined" startIcon={<CloudDownloadIcon />} onClick={handleExportExcel} size="small">
                Exportar
              </Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/ventas/vendedores/nuevo')}>
                Nuevo Vendedor
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, legajo o número..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {vendedores.map((vendedor) => (
                <Card key={vendedor.nroVendedor} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {vendedor.nroVendedor} - {vendedor.nombreVendedor}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Legajo: {vendedor.legajo || '-'}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/ventas/vendedores/${vendedor.nroVendedor}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(vendedor.nroVendedor)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>N° Vendedor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Legajo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Comisión %</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendedores.map((vendedor) => (
                    <TableRow key={vendedor.nroVendedor} hover>
                      <TableCell>{vendedor.nroVendedor}</TableCell>
                      <TableCell>{vendedor.nombreVendedor}</TableCell>
                      <TableCell>{vendedor.legajo || '-'}</TableCell>
                      <TableCell>{vendedor.tipoVendedor || '-'}</TableCell>
                      <TableCell>{vendedor.comisionPorc ? `${vendedor.comisionPorc}%` : '-'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/ventas/vendedores/${vendedor.nroVendedor}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(vendedor.nroVendedor)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Vendedores;
